import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API, type ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, fecha, num, pct, toDateValue } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_DATE, FMT_INT, FMT_NUM, FMT_PCT, fileNameConFecha } from '../lib/xlsx';
import type { Lote } from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { FilterBar } from '../components/FilterBar';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { IndicadoresFinancieros, distribucionDesdeLote } from '../components/IndicadoresFinancieros';
import { precioSugerido, useParametros } from '../lib/finanzas';

type RespuestaLotes = {
  items: Lote[];
  total: number;
  agregados: { paquetes: number; ingreso: number; costo: number; ganancia: number };
};

export function ProduccionDelDiaPage() {
  const { tiene } = useAuth();
  const { margenObjetivo } = useParametros();
  const qc = useQueryClient();
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [seleccion, setSeleccion] = useState<Lote | null>(null);
  const [editando, setEditando] = useState<Lote | null>(null);

  const q = useQuery({
    queryKey: ['lotes', desde, hasta],
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      params.set('limit', '100');
      return API.get<RespuestaLotes>(`/lotes?${params.toString()}`);
    },
    enabled: tiene('lotes:read'),
  });

  const del = useMutation({
    mutationFn: (id: string) => API.del(`/lotes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lotes'] });
      qc.invalidateQueries({ queryKey: ['ultimos-lotes'] });
      qc.invalidateQueries({ queryKey: ['movimientos'] });
      qc.invalidateQueries({ queryKey: ['saldo'] });
      setSeleccion(null);
    },
    onError: (err: unknown) => {
      const e = err as { status?: number; message?: string };
      alert(`No se pudo eliminar: ${e.message ?? 'error desconocido'}${e.status ? ` (HTTP ${e.status})` : ''}`);
    },
  });

  const a = q.data?.agregados;
  const activeFilters = (desde ? 1 : 0) + (hasta ? 1 : 0);

  function exportarXlsx() {
    const items = q.data?.items ?? [];
    if (items.length === 0) return;
    downloadXlsx(fileNameConFecha('produccion-del-dia'), [
      {
        name: 'Producción del día',
        rows: items,
        columns: [
          { header: 'Fecha', value: (l) => toDateValue(l.fecha), width: 12, numFmt: FMT_DATE },
          { header: 'Kilos maíz', value: (l) => Number(l.kilosMaiz), width: 10, numFmt: FMT_NUM },
          { header: 'Arepas brutas', value: (l) => l.arepasBrutas, width: 10, numFmt: FMT_INT },
          { header: 'Merma', value: (l) => l.arepasMerma, width: 8, numFmt: FMT_INT },
          { header: 'Arepas netas', value: (l) => l.arepasNetas, width: 10, numFmt: FMT_INT },
          { header: 'Arepas/paq', value: (l) => l.arepasPorPaquete, width: 10, numFmt: FMT_INT },
          { header: 'Paquetes', value: (l) => l.paquetes, width: 10, numFmt: FMT_INT },
          { header: 'Gas (lb)', value: (l) => Number(l.gasLb), width: 10, numFmt: FMT_NUM },
          { header: 'Bolsas', value: (l) => l.bolsas, width: 10, numFmt: FMT_INT },
          { header: 'Agua (L)', value: (l) => Number(l.aguaL), width: 10, numFmt: FMT_NUM },
          { header: 'Luz (kWh)', value: (l) => Number(l.luzKWh ?? 0), width: 10, numFmt: FMT_NUM },
          { header: 'Personas', value: (l) => l.personas, width: 9, numFmt: FMT_INT },
          { header: 'Horas trabajo', value: (l) => Number(l.horasTrabajo), width: 10, numFmt: FMT_NUM },
          { header: 'Precio venta/paq', value: (l) => Number(l.precioVentaPaq), width: 14, numFmt: FMT_COP },
          { header: 'Costo variable/paq', value: (l) => Number(l.costoVariablePaq), width: 14, numFmt: FMT_COP },
          { header: 'Costo fijo/paq', value: (l) => Number(l.costoFijoPaq), width: 14, numFmt: FMT_COP },
          { header: 'Costo total/paq', value: (l) => Number(l.costoTotalPaq), width: 14, numFmt: FMT_COP },
          { header: 'Precio sugerido/paq', value: (l) => precioSugerido(Number(l.costoTotalPaq)), width: 16, numFmt: FMT_COP },
          { header: 'Ganancia/paq', value: (l) => Number(l.gananciaPaq), width: 14, numFmt: FMT_COP },
          { header: 'Margen %', value: (l) => Number(l.margenPct), width: 10, numFmt: FMT_PCT },
          { header: 'Ingreso total', value: (l) => Number(l.ingresoTotal), width: 16, numFmt: FMT_COP },
          { header: 'Costo total lote', value: (l) => Number(l.costoTotalLote), width: 16, numFmt: FMT_COP },
          { header: 'Ganancia total', value: (l) => Number(l.gananciaTotal), width: 16, numFmt: FMT_COP },
          { header: 'Notas', value: (l) => l.notas ?? '', width: 30 },
        ],
      },
    ]);
  }

  const columns: Column<Lote>[] = [
    { key: 'fecha', header: 'Fecha', render: (l) => fecha(l.fecha), mobile: 'primary' },
    { key: 'paq', header: 'Paq.', render: (l) => <b>{l.paquetes}</b>, mobile: 'secondary', align: 'right' },
    { key: 'kg', header: 'Kg', render: (l) => num(Number(l.kilosMaiz), 2), align: 'right' },
    { key: 'brutas', header: 'Brutas', render: (l) => l.arepasBrutas, align: 'right' },
    { key: 'netas', header: 'Netas', render: (l) => l.arepasNetas, align: 'right' },
    { key: 'costoPaq', header: 'Costo/paq', render: (l) => cop(Number(l.costoTotalPaq)), align: 'right' },
    {
      key: 'ganPaq',
      header: 'Ganancia/paq',
      render: (l) => (
        <span className={Number(l.gananciaPaq) >= 0 ? 'text-emerald-700' : 'text-red-600'}>
          {cop(Number(l.gananciaPaq))}
        </span>
      ),
      align: 'right',
    },
    { key: 'margen', header: 'Margen', render: (l) => pct(Number(l.margenPct)), align: 'right' },
    { key: 'ingreso', header: 'Ingreso', render: (l) => cop(Number(l.ingresoTotal)), align: 'right' },
    {
      key: 'ganTotal',
      header: 'Ganancia total',
      render: (l) => (
        <span className={Number(l.gananciaTotal) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
          {cop(Number(l.gananciaTotal))}
        </span>
      ),
      align: 'right',
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Historial de producción"
        subtitle="Lotes guardados desde la calculadora. Podés editarlos o eliminarlos."
      />

      <div className="card p-3 sm:p-4 space-y-3">
        <FilterBar activeCount={activeFilters} title="Rango de fechas">
          <div>
            <label className="label">Desde</label>
            <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <label className="label">Hasta</label>
            <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
          {(desde || hasta) && (
            <div className="sm:self-end">
              <button type="button" className="btn-secondary w-full sm:w-auto" onClick={() => { setDesde(''); setHasta(''); }}>
                Limpiar
              </button>
            </div>
          )}
        </FilterBar>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-600">
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-x-4 gap-y-1">
            <span><b>{q.data?.total ?? 0}</b> lotes</span>
            <span>{num(a?.paquetes ?? 0, 0)} paq</span>
            <span>{cop(a?.ingreso)} ingresos</span>
            <span className={Number(a?.ganancia ?? 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
              {cop(a?.ganancia)} gan.
            </span>
          </div>
          <button
            type="button"
            className="btn-secondary w-full sm:w-auto"
            onClick={exportarXlsx}
            disabled={!q.data?.items?.length}
            title="Descargar como .xlsx"
          >
            ⬇ Exportar .xlsx
          </button>
        </div>
      </div>

      <div className="card">
        <ResponsiveTable<Lote>
          columns={columns}
          rows={q.data?.items}
          rowKey={(l) => l.id}
          loading={q.isLoading}
          empty="Sin lotes en este rango."
          actions={(l) => (
            <>
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => setSeleccion(l)}
              >
                Ver detalle
              </button>
              {tiene('lotes:write') && (
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setEditando(l)}
                >
                  Editar
                </button>
              )}
              {tiene('lotes:write') && (
                <button
                  type="button"
                  className="btn-danger text-xs"
                  onClick={() => {
                    if (confirm(`¿Eliminar lote del ${fecha(l.fecha)}?`)) del.mutate(l.id);
                  }}
                >
                  Eliminar
                </button>
              )}
            </>
          )}
        />
      </div>

      {editando && (
        <EditarLoteModal
          lote={editando}
          onClose={() => setEditando(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['lotes'] });
            qc.invalidateQueries({ queryKey: ['ultimos-lotes'] });
            qc.invalidateQueries({ queryKey: ['movimientos'] });
            qc.invalidateQueries({ queryKey: ['saldo'] });
            setEditando(null);
          }}
        />
      )}

      {seleccion && (
        <div className="card p-4 sm:p-5 space-y-3">
          <div className="flex justify-between items-center gap-2">
            <h2 className="font-semibold text-base">Detalle · {fecha(seleccion.fecha)}</h2>
            <button type="button" className="text-sm text-slate-500 hover:text-slate-800 tap px-2" onClick={() => setSeleccion(null)}>
              Cerrar
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-3">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Datos operativos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 text-sm">
                  <Field label="Kilos de maíz" value={`${num(Number(seleccion.kilosMaiz), 2)} kg`} />
                  <Field label="Arepas brutas" value={String(seleccion.arepasBrutas)} />
                  <Field label="Merma" value={String(seleccion.arepasMerma)} />
                  <Field label="Arepas netas" value={String(seleccion.arepasNetas)} />
                  <Field label="Arepas por paquete" value={String(seleccion.arepasPorPaquete)} />
                  <Field label="Paquetes" value={String(seleccion.paquetes)} />
                  <Field label="Gas" value={`${num(Number(seleccion.gasLb), 2)} lb`} />
                  <Field label="Bolsas" value={String(seleccion.bolsas)} />
                  <Field label="Agua" value={`${num(Number(seleccion.aguaL), 2)} L`} />
                  <Field label="Luz" value={`${num(Number(seleccion.luzKWh ?? 0), 2)} kWh`} />
                  <Field label="Personas" value={String(seleccion.personas)} />
                  <Field label="Horas" value={`${num(Number(seleccion.horasTrabajo), 2)} h`} />
                </div>
              </div>
              {seleccion.notas && (
                <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded p-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Notas</div>
                  {seleccion.notas}
                </div>
              )}
            </div>

            <div>
              <IndicadoresFinancieros
                lote={seleccion}
                margenObjetivo={margenObjetivo}
                distribucion={distribucionDesdeLote(seleccion)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-medium text-sm break-words">{value}</div>
    </div>
  );
}

type EditForm = {
  fecha: string;
  kilosMaiz: string;
  arepasBrutas: string;
  arepasMerma: string;
  arepasPorPaquete: string;
  aguaL: string;
  luzKWh: string;
  gasLb: string;
  bolsas: string;
  personas: string;
  horasTrabajo: string;
  precioVentaPaq: string;
  notas: string;
};

function loteToForm(l: Lote): EditForm {
  return {
    fecha: l.fecha.slice(0, 10),
    kilosMaiz: String(l.kilosMaiz),
    arepasBrutas: String(l.arepasBrutas),
    arepasMerma: String(l.arepasMerma),
    arepasPorPaquete: String(l.arepasPorPaquete),
    aguaL: String(l.aguaL),
    luzKWh: String(l.luzKWh ?? '0'),
    gasLb: String(l.gasLb),
    bolsas: String(l.bolsas),
    personas: String(l.personas),
    horasTrabajo: String(l.horasTrabajo),
    precioVentaPaq: String(l.precioVentaPaq),
    notas: l.notas ?? '',
  };
}

function toNum(v: string): number {
  const n = Number(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function EditarLoteModal({
  lote,
  onClose,
  onSaved,
}: {
  lote: Lote;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState<EditForm>(() => loteToForm(lote));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function guardar() {
    setError(null);
    setGuardando(true);
    try {
      const payload = {
        fecha: f.fecha,
        kilosMaiz: toNum(f.kilosMaiz),
        arepasBrutas: Math.round(toNum(f.arepasBrutas)),
        arepasMerma: Math.round(toNum(f.arepasMerma)),
        arepasPorPaquete: Math.max(1, Math.round(toNum(f.arepasPorPaquete))),
        aguaL: toNum(f.aguaL),
        luzKWh: toNum(f.luzKWh),
        gasLb: toNum(f.gasLb),
        bolsas: Math.round(toNum(f.bolsas)),
        personas: Math.round(toNum(f.personas)),
        horasTrabajo: toNum(f.horasTrabajo),
        precioVentaPaq: toNum(f.precioVentaPaq),
        notas: f.notas || undefined,
      };
      await API.put<Lote>(`/lotes/${lote.id}`, payload);
      onSaved();
    } catch (err) {
      setError((err as ApiError).message ?? 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  }

  function upd(key: keyof EditForm, v: string) {
    setF((s) => ({ ...s, [key]: v }));
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-200">
          <h2 className="font-semibold">Editar lote · {fecha(lote.fecha)}</h2>
          <button
            type="button"
            className="text-slate-500 hover:text-slate-800 text-xl leading-none px-2"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
            Al guardar se recalculan costos y el movimiento de ingreso se regenera con los precios <b>actuales</b> de los insumos.
          </div>

          <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
            <FieldEdit label="Fecha" type="date" value={f.fecha} onChange={(v) => upd('fecha', v)} />
            <FieldEdit label="Kilos de maíz" unidad="kg" value={f.kilosMaiz} onChange={(v) => upd('kilosMaiz', v)} />
            <FieldEdit label="Arepas brutas" unidad="und" value={f.arepasBrutas} onChange={(v) => upd('arepasBrutas', v)} />
            <FieldEdit label="Merma" unidad="und" value={f.arepasMerma} onChange={(v) => upd('arepasMerma', v)} />
            <FieldEdit label="Arepas por paquete" value={f.arepasPorPaquete} onChange={(v) => upd('arepasPorPaquete', v)} />
            <FieldEdit label="Precio venta" unidad="COP/paq" value={f.precioVentaPaq} onChange={(v) => upd('precioVentaPaq', v)} />
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Insumos consumidos</h3>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
              <FieldEdit label="Gas" unidad="lb" value={f.gasLb} onChange={(v) => upd('gasLb', v)} />
              <FieldEdit label="Bolsas" unidad="und" value={f.bolsas} onChange={(v) => upd('bolsas', v)} />
              <FieldEdit label="Agua" unidad="L" value={f.aguaL} onChange={(v) => upd('aguaL', v)} />
              <FieldEdit label="Luz" unidad="kWh" value={f.luzKWh} onChange={(v) => upd('luzKWh', v)} />
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide text-slate-500 mb-2">Trabajo</h3>
            <div className="grid grid-cols-2 gap-3">
              <FieldEdit label="Personas" value={f.personas} onChange={(v) => upd('personas', v)} />
              <FieldEdit label="Horas" unidad="h" value={f.horasTrabajo} onChange={(v) => upd('horasTrabajo', v)} />
            </div>
          </div>

          <div>
            <label className="label">Notas</label>
            <textarea
              className="input min-h-[60px]"
              value={f.notas}
              onChange={(e) => upd('notas', e.target.value)}
              placeholder="Observaciones del lote"
            />
          </div>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 sm:px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldEdit({
  label, value, onChange, type = 'number', unidad,
}: {
  label: string; value: string; onChange: (v: string) => void; type?: 'number' | 'date' | 'text'; unidad?: string;
}) {
  return (
    <div>
      <label className="label">
        {label}
        {unidad && <span className="text-slate-400 font-normal"> ({unidad})</span>}
      </label>
      <input
        type={type}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onWheel={type === 'number' ? (e) => e.currentTarget.blur() : undefined}
        inputMode={type === 'number' ? 'decimal' : undefined}
        step={type === 'number' ? 'any' : undefined}
        min={type === 'number' ? '0' : undefined}
      />
    </div>
  );
}
